import { staticCategories } from "examples/ValuePack/ValuePackStaticData";

export const getCategoryById = (id) => {
  return staticCategories.find(category => category.id === id);
};

export const getCategoriesByIds = (ids = [], allCategories = []) => {
  if (!Array.isArray(ids) || !Array.isArray(allCategories)) return [];
  
  return allCategories.filter(cat => ids.map(String).includes(String(cat.id)));
};

export const getFeaturesByCategory = (categoryId) => {
  const category = getCategoryById(categoryId);
  return category ? category.features : [];
};

export const getAdditionalFeaturesByCategory = (categoryId) => {
  const category = getCategoryById(categoryId);
  return category ? category.additionalFeatures : [];
};

export const generateValuePackId = () => {
  return `vp${Date.now()}`;
};

export const groupTiersByDurationCountry = (tiers) => {
  if (!tiers || !Array.isArray(tiers)) return {};
  
  const groups = {};
  
  tiers.forEach(tier => {
    if (tier.countryCode && tier.duration !== undefined) {
      const key = `${tier.duration}-${tier.countryCode}`;
      
      if (!groups[key]) {
        groups[key] = {
          duration: tier.duration,
          country: tier.countryCode,
          tiers: []
        };
      }
      groups[key].tiers.push(tier);
    }
  });

  // Sort tiers within each group by tier type
  Object.keys(groups).forEach(key => {
    groups[key].tiers.sort((a, b) => {
      const order = { 'Low': 1, 'Mid': 2, 'High': 3 };
      return (order[a.tierType] || 0) - (order[b.tierType] || 0);
    });
  });

  return groups;
};

export const calculatePriceRange = (tiers, country) => {
  if (!tiers || tiers.length === 0) {
    return { min: 0, max: 0, currency: 'USD' };
  }
  
  const countryTiers = tiers.filter(tier => tier.countryCode === country);
  
  if (countryTiers.length === 0) {
    return { min: 0, max: 0, currency: 'USD' };
  }
  
  // Get all prices for this country
  const prices = countryTiers
    .map(tier => tier.currentPrice)
    .filter(price => price !== undefined && price !== null)
    .map(price => typeof price === 'string' ? parseFloat(price) : price);
  
  if (prices.length === 0) {
    return { min: 0, max: 0, currency: 'USD' };
  }
  
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  
  // Get currency from first tier or use default
  const firstTier = countryTiers[0];
  const currency = firstTier.currency || 'USD';
  
  return { 
    min: minPrice, 
    max: maxPrice, 
    currency: currency 
  };
};

// Data Formatting Functions
export const formatCurrency = (price, currency, locale = 'en-US') => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(price);
  } catch (error) {
    return `${currency} ${price}`;
  }
};

export const formatDuration = (months) => {
  if (months === 1) return '1 Month';
  if (months === 12) return '1 Year';
  return `${months} Months`;
};

// Utility Functions for Data Processing
export const getTotalSavings = (originalPrice, discountedPrice) => {
  return originalPrice - discountedPrice;
};

export const calculateDiscountPercentage = (originalPrice, discountedPrice) => {
  if (originalPrice === 0) return 0;
  return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
};

export const isValuePackActive = (valuePack) => {
  if (!valuePack.active) return false;
  
  const now = new Date();
  const publishDate = valuePack.publishedAt ? new Date(valuePack.publishedAt) : null;
  const expiryDate = valuePack.expiresAt ? new Date(valuePack.expiresAt) : null;
  
  if (publishDate && publishDate > now) return false;
  if (expiryDate && expiryDate < now) return false;
  
  return true;
};

// Data Transformation Functions
export const transformTiersForDisplay = (tiers, selectedCountry = 'US') => {
  if (!tiers || !Array.isArray(tiers)) return [];
  
  return tiers
    .filter(tier => tier.countryPricing && tier.countryPricing[selectedCountry])
    .map(tier => ({
      ...tier,
      pricing: tier.countryPricing[selectedCountry],
      formattedPrice: formatCurrency(
        tier.countryPricing[selectedCountry].price,
        tier.countryPricing[selectedCountry].currency
      )
    }))
    .sort((a, b) => a.pricing.price - b.pricing.price);
};

export const getPopularTier = (tiers) => {
  if (!tiers || !Array.isArray(tiers)) return null;
  return tiers.find(tier => tier.isPopular) || null;
};

// Default duration/country filter for tier views: prefer India + 3 months,
// falling back to "all" independently for whichever isn't available.
export const getDefaultTierFilter = (tiers = [], options = {}) => {
  const { preferredCountry = 'IN', preferredDuration = 3 } = options;
  const list = Array.isArray(tiers) ? tiers : [];

  const hasPreferredCountry = list.some(tier => tier.countryCode === preferredCountry);
  const hasPreferredDuration = list.some(tier => Number(tier.duration) === Number(preferredDuration));

  return {
    country: hasPreferredCountry ? preferredCountry : 'all',
    duration: hasPreferredDuration ? preferredDuration : 'all',
  };
};
